// T7 (LAUNCH_PLAN.md) — passive banner for unverified email/password users.
//
// Behavior:
//   - Renders only when firebaseUser exists, is not anonymous, and
//     firebaseUser.emailVerified === false.
//   - Google sign-ins arrive with emailVerified === true → banner is
//     never shown for them.
//   - Banner is dismissible per session (state-only; reappears on next
//     sign-in until verified).
//   - "Resend" has a 60s UX cooldown (in addition to Firebase Auth's
//     built-in rate limits).
//   - "I've verified" calls reload + getIdToken(true) so callable /
//     rule gates see the new claim immediately. If still unverified,
//     shows a helpful nudge ("We don't see it yet — check spam?").

import React, { useState } from 'react';
import { useCurrentUser } from '../contexts/UserContext';
import {
  sendEmailVerificationLink,
  refreshVerifiedClaim,
  useEmailVerificationCooldown,
  RESEND_COOLDOWN_SECONDS,
} from '../lib/emailVerification';

type Status =
  | { kind: 'idle' }
  | { kind: 'sent' }
  | { kind: 'send_failed'; message: string }
  | { kind: 'verifying' }
  | { kind: 'verify_pending' }
  | { kind: 'verify_failed'; message: string };

export default function EmailVerificationBanner() {
  const { firebaseUser, refreshProfile } = useCurrentUser();
  const [dismissed, setDismissed] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const cooldown = useEmailVerificationCooldown();

  // Hide for: signed-out, anonymous, already-verified, user-dismissed.
  // Google users hit the "already-verified" branch automatically.
  if (
    !firebaseUser ||
    firebaseUser.isAnonymous ||
    firebaseUser.emailVerified ||
    dismissed
  ) {
    return null;
  }

  async function handleResend() {
    if (!firebaseUser || cooldown.remaining > 0) return;
    setStatus({ kind: 'idle' });
    try {
      await sendEmailVerificationLink(firebaseUser);
      cooldown.start();
      setStatus({ kind: 'sent' });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Could not send verification email.';
      setStatus({ kind: 'send_failed', message });
    }
  }

  async function handleConfirm() {
    if (!firebaseUser) return;
    setStatus({ kind: 'verifying' });
    try {
      const ok = await refreshVerifiedClaim(firebaseUser);
      if (ok) {
        await refreshProfile();
        // Banner will unmount naturally because firebaseUser.emailVerified
        // is now true.
      } else {
        setStatus({ kind: 'verify_pending' });
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Could not refresh verification status.';
      setStatus({ kind: 'verify_failed', message });
    }
  }

  const isResendDisabled = cooldown.remaining > 0;
  const isVerifying = status.kind === 'verifying';

  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full bg-amber-950/80 border-b border-amber-700 text-amber-100 px-4 py-2 text-sm"
    >
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex-1">
          <span className="font-medium">Verify your email</span>
          <span className="text-amber-200/80">
            {' — '}
            we sent a verification link to <strong>{firebaseUser.email}</strong>.
            Some features (publishing, tipping, payouts) require verification.
          </span>
          {status.kind === 'sent' && (
            <span className="ml-2 text-amber-300">Resent. Check your inbox.</span>
          )}
          {status.kind === 'send_failed' && (
            <span className="ml-2 text-red-300">Could not send: {status.message}</span>
          )}
          {status.kind === 'verify_pending' && (
            <span className="ml-2 text-amber-300">
              We don't see it yet — check spam, then try again in a moment.
            </span>
          )}
          {status.kind === 'verify_failed' && (
            <span className="ml-2 text-red-300">Refresh failed: {status.message}</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleResend}
            disabled={isResendDisabled || isVerifying}
            className="px-3 py-1 rounded-md bg-amber-700 hover:bg-amber-600 text-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={isResendDisabled ? `Resend in ${cooldown.remaining}s` : 'Resend verification email'}
          >
            {isResendDisabled ? `Resend in ${cooldown.remaining}s` : 'Resend email'}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isVerifying}
            className="px-3 py-1 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isVerifying ? 'Checking…' : "I've verified"}
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="text-amber-200/80 hover:text-white text-xs"
            aria-label="Dismiss"
            title={`Dismiss for this session (reminder returns next sign-in; cooldown ${RESEND_COOLDOWN_SECONDS}s)`}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
